import { useEffect, useState, type JSX } from 'react';
import { Button, IconArrowRight, IconBellNotification, IconChatTwoBubblesOval, IconCheck, cn, useNotificationsPort, useSourceDeMentions, type NotificationBoiteView } from '@taki/ui';

import { resumeAvecNoms } from '../centre/mentionsNommees.js';

import { RUBRIQUES_DE_LA_BOITE, lieuDeMention, quand } from './source.js';

/**
 * LA BOITE DE NOTIFICATIONS (maquette hub-c) : la liste que la cloche du dock
 * ouvre, et que le centre de communication monte en zone 5 de sa source
 * `notifications`. Une boite d'ACTIVITE (P21, retouches du 06/10/2026) :
 * « Activite recente », groupes discrets, une icone par type, titre, extrait,
 * lieu et heure hierarchises, sans filet entre les lignes, survol leger ; le
 * non lu est en gras avec un point lime. Une ligne MENE a son objet et se marque lue ; « Tout marquer
 * lu » est le seul geste global. Le port vient de `@taki/ui` : aucun Firebase ici.
 */
export interface NotificationsBoiteProps {
  /** Ouvre l'objet d'une notification (`/app/...`) ; le shell navigue. */
  onOuvrir?: (chemin: string, notification: NotificationBoiteView) => void;
  /** Lignes chargees au plus. */
  limite?: number;
  className?: string;
}

export function NotificationsBoite({ onOuvrir, limite = 30, className }: NotificationsBoiteProps): JSX.Element {
  const port = useNotificationsPort();
  // Le titre et l'extrait d'une notification disent les mentions par leurs noms (passe 2, ecart J).
  const { entrees } = useSourceDeMentions();
  const [lignes, setLignes] = useState<NotificationBoiteView[] | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const total = port?.nonLus.total ?? 0;

  // Relire a chaque changement du total : une notification arrive, ou est lue ailleurs.
  useEffect(() => {
    if (port === null) return;
    let vivant = true;
    port.boite({ limit: limite }).then(
      (liste) => { if (vivant) { setLignes(liste); setErreur(null); } },
      (cause: unknown) => { if (vivant) setErreur(cause instanceof Error ? cause.message : String(cause)); },
    );
    return () => { vivant = false; };
  }, [port, total, limite]);

  if (port === null) {
    return <p className="px-4 py-3 text-sm text-ink-500">Les notifications ne sont pas branchées sur cet hôte.</p>;
  }

  const ouvrir = (n: NotificationBoiteView): void => {
    if (!n.lu) {
      setLignes((liste) => liste?.map((l) => (l.id === n.id ? { ...l, lu: true } : l)) ?? null);
      void port.marquerLu(n.id).catch(cause => { setLignes(l => l?.map(x => x.id === n.id ? { ...x, lu: false } : x) ?? null); setErreur(String(cause)); });
    }
    // Un sujet de Lia n'a pas d'adresse mais un objet : le centre de communication l'ouvre.
    // Une mention (ou Lia dans une conversation) s'ouvre dans le centre : le shell lit `chemin`, `objet` et `contexte`.
    if (n.chemin || n.objet || n.contexte) onOuvrir?.(n.chemin, n);
  };
  const toutLire = async (): Promise<void> => {
    setLignes((liste) => liste?.map((l) => ({ ...l, lu: true })) ?? null);
    try { await port.toutMarquerLu(); } catch (cause) { setErreur(cause instanceof Error ? cause.message : String(cause)); }
  };

  // P21 (retouches validees du 06/10/2026) : la PRESENTATION d'une boite d'activite ; le contenu (les seules
  // notifications internes hors conversations) et ses gestes restent ceux du socle notifications.
  return (
    <div className={cn('flex min-h-0 flex-col bg-surface', className)}>
      <div className="flex shrink-0 flex-wrap items-center justify-between gap-2 px-5 pb-3 pt-5">
        <div className="flex items-center gap-2">
          <h2 className="text-sm font-semibold text-ink-900">Activité récente</h2>
          {total > 0 && <span className="text-xs text-ink-500">{total} non lue{total > 1 ? 's' : ''}</span>}
        </div>
        {total === 0
          ? <span className="flex items-center gap-1.5 text-xs text-ink-500"><IconCheck size={13} />Tout est lu</span>
          : <Button size="sm" variant="secondary" onClick={() => void toutLire()}><IconCheck size={13} />Tout marquer lu</Button>}
      </div>
      {erreur && <p className="px-5 py-2 text-sm text-danger" role="alert">{erreur}</p>}
      {lignes === null ? (
        <p className="px-5 py-3 text-sm text-ink-500">Chargement...</p>
      ) : lignes.length === 0 ? (
        <div className="flex min-h-0 flex-1 flex-col items-center justify-center px-6 py-10 text-center">
          <span aria-hidden="true" className="flex h-12 w-12 items-center justify-center rounded-2xl bg-surface-3 text-ink-500"><IconBellNotification size={22} /></span>
          <h3 className="mt-4 text-sm font-semibold text-ink-900">Vous êtes à jour</h3>
          <p className="mt-2 max-w-xs text-sm leading-5 text-ink-500">Aucune notification interne. Les mentions et les règles métier arrivent ici.</p>
        </div>
      ) : (
        <div className="min-h-0 flex-1 space-y-5 overflow-y-auto px-2 pb-5 pt-1">
          {RUBRIQUES_DE_LA_BOITE.map((rubrique) => {
            const siennes = lignes.filter((l) => l.rubrique === rubrique.id);
            if (siennes.length === 0) return null;
            return (
              <section key={rubrique.id} aria-label={rubrique.titre}>
                <h3 className="px-3 pb-1.5 text-xs font-semibold text-ink-500">{rubrique.titre}</h3>
                <ul className="space-y-0.5">
                  {siennes.map((n) => {
                    const lieu = n.contexte ? lieuDeMention(n.contexte)
                      : n.objet?.type === 'conversation' ? 'Conversation' : n.objet?.type === 'contents' ? 'Contenu' : null;
                    return (
                      <li key={n.id}>
                        <button
                          type="button"
                          onClick={() => ouvrir(n)}
                          data-non-lue={!n.lu || undefined}
                          className="group flex w-full items-start gap-3 rounded-lg p-3 text-left hover:bg-surface-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-accent-border"
                        >
                          <span aria-hidden="true" className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-surface-3 text-ink-600">
                            {rubrique.id === 'citation' ? <span className="text-base font-medium leading-none">@</span>
                              : rubrique.id === 'message' ? <IconChatTwoBubblesOval size={16} /> : <IconBellNotification size={16} />}
                          </span>
                          <span className="min-w-0 flex-1">
                            <span className={cn('block break-words text-sm leading-5 text-ink-900', n.lu ? 'font-medium' : 'font-semibold')}>{resumeAvecNoms(n.sujet, entrees)}</span>
                            {n.corps && <span className="mt-0.5 line-clamp-2 block break-words text-[0.8125rem] leading-5 text-ink-600">{resumeAvecNoms(n.corps, entrees)}</span>}
                            <span className="mt-1.5 flex min-w-0 items-center gap-1.5 text-[0.6875rem] leading-4 text-ink-500">
                              {lieu && <><span className="truncate">{lieu}</span><span aria-hidden="true">·</span></>}
                              <time className="shrink-0" dateTime={n.createdAt?.toISOString()}>{quand(n.createdAt)}</time>
                            </span>
                          </span>
                          <span className="flex w-3.5 shrink-0 flex-col items-center gap-3 pt-1.5">
                            {!n.lu && <span aria-label="Non lue" className="h-1.5 w-1.5 rounded-full bg-accent-dot" />}
                            <IconArrowRight size={14} className="text-ink-500 opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100" />
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </section>
            );
          })}
        </div>
      )}
    </div>
  );
}
